import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { ERP_MENU_PATHS } from "@eurostorm/shared";
import { CarLogo } from "../components/common/CarLogo.js";
import { TeamNoticesPopup } from "../components/domain/TeamNoticesPopup.js";
import { MyTasksList } from "../components/domain/MyTasksList.js";
import {
  OPERATION_TONE,
  useOperationLabels,
  type OperationStatus,
} from "../components/domain/OperationBoard.js";
import { LanguageSwitcher } from "../components/common/LanguageSwitcher.js";
import { formatFrenchPlate } from "../lib/plate-format.js";
import { CameraScanner, cameraAoVivoDisponivel } from "../components/portal/CameraScanner.js";
import { useAuth } from "../hooks/useAuth.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useT } from "../i18n/index.js";
import type { Locale } from "../i18n/messages.js";
import { trDepose, trMethod, trNote, trPanel } from "../lib/devis-i18n.js";
import {
  ChecklistExtras,
  GuidedPhotoLine,
  MONTAGE_PHOTO_SLOTS,
  type ChecklistDraft,
  type PhotoSlotKind,
} from "../components/portal/ChecklistExtras.js";
import { compressImageToBase64 } from "../lib/image-compress.js";
import {
  ExpertiseCheckCard,
  type ExpertiseState,
} from "../components/portal/ExpertiseCheckCard.js";
import { ReceptionDocsCard, type DocCat } from "../components/portal/ReceptionDocsCard.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useQueryClient } from "@tanstack/react-query";

/**
 * F-chantier-manager — portal do GERENTE DE PROJETO (chef de chantier),
 * rota /chantier, mobile-first, fora do ErpShell. Ele recebe o veículo no
 * chantier (check-list de RECEPÇÃO: danos pré-existentes + franquia) e na
 * saída faz a ENTREGA (controle do trabalho). Vê o DEVIS COMPLETO, mas
 * NUNCA comissões (nem do técnico, nem a remise do cliente).
 */

type DevisBd = {
  totalDsp?: number;
  degarnissage?: number;
  carrosserie?: number;
  pieces?: number;
  pareBrise?: number;
  grandTotalHt?: number;
  tva?: number;
  totalTtc?: number;
  panelLines?: Array<{
    panel: string;
    method: string;
    impacts: number | null;
    sizeClass: string | null;
    note: string | null;
  }>;
  deposeRepose?: string | null;
  impactsTotal?: number | null;
};

export default function ChantierPortal() {
  const t = useT();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const meQuery = trpc.chantierPortal.me.useQuery(undefined, { enabled: !!user });
  // F-recados-equipe (CEO 2026-10-29) — o gerente de chantier também recebe os
  // recados do escritório (pop-up ao entrar; Ciente / Feito).
  const recadosQ = trpc.erpNotices.mine.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5 * 60_000,
  });
  const recados = recadosQ.data?.rows ?? [];
  const tarefasQ = trpc.tasks.mine.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5 * 60_000,
  });
  // F-tarefas-popup (CEO 2026-10-29) — tarefas atrasadas/de hoje abrem o pop-up.
  const tarefasPopup = (tarefasQ.data?.rows ?? []).filter((r) => r.popup);
  const [recadosAbertos, setRecadosAbertos] = useState(false);
  const [recadosAuto, setRecadosAuto] = useState(false);
  useEffect(() => {
    if (!recadosAuto && (recados.length > 0 || tarefasPopup.length > 0)) {
      setRecadosAbertos(true);
      setRecadosAuto(true);
    }
  }, [recados.length, tarefasPopup.length, recadosAuto]);
  const companies = meQuery.data?.companies ?? [];
  const [companyId, setCompanyId] = useState<number | null>(null);
  const effectiveCompanyId = companyId ?? companies[0]?.companyId ?? null;

  const [mode, setMode] = useState<"plate" | "vin">("plate");
  const [plate, setPlate] = useState("");
  const [vin, setVin] = useState("");
  const [searched, setSearched] = useState<{ value: string; mode: "plate" | "vin" } | null>(null);
  const [feedback, setFeedback] = useState<{ tone: "ok" | "warn" | "error"; text: string } | null>(
    null,
  );

  // Câmera → OCR no aparelho (mesma infra do portal do técnico).
  const [scanning, setScanning] = useState<"plate" | "vin" | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const photoKindRef = useRef<"plate" | "vin">("plate");
  // CEO 2026-10-18 — ao tocar numa placa (alarmes ou quadro) a tela ia para o
  // TOPO, e com dois alarmes cheios (Saint-Just: 19 chips) o card do carro
  // ficava fora da vista: "cliquei e nada aconteceu". Agora rola até a busca,
  // que é logo acima do card do carro.
  const buscaRef = useRef<HTMLFormElement | null>(null);
  const irParaBusca = () =>
    buscaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  // F-scanner (brief CEO 2026-10-04) — câmera AO VIVO quando o navegador
  // permite (HTTPS + getUserMedia); senão, a foto de antes.
  const [liveScan, setLiveScan] = useState<"plaque" | "vin" | null>(null);
  function openCamera(kind: "plate" | "vin") {
    if (cameraAoVivoDisponivel()) {
      setLiveScan(kind === "plate" ? "plaque" : "vin");
      return;
    }
    photoKindRef.current = kind;
    photoInputRef.current?.click();
  }
  async function handlePhoto(file: File) {
    const kind = photoKindRef.current;
    setScanning(kind);
    setFeedback(null);
    try {
      const { recognizeFromPhoto } = await import("../lib/plate-ocr.js");
      const result = await recognizeFromPhoto(file, kind);
      if (result) {
        setMode(kind);
        if (kind === "plate") setPlate(result);
        else setVin(result);
        setSearched({ value: result, mode: kind });
      } else {
        setFeedback({ tone: "warn", text: t("chantier.scanFail") });
      }
    } catch {
      setFeedback({ tone: "error", text: t("chantier.scanFail") });
    } finally {
      setScanning(null);
    }
  }

  const searchQuery = trpc.chantierPortal.findByPlate.useQuery(
    {
      companyId: effectiveCompanyId ?? 0,
      plate: searched?.value ?? "",
      mode: searched?.mode ?? "plate",
    },
    { enabled: effectiveCompanyId != null && searched != null && searched.value.length >= 4 },
  );
  // F-chantier-entity — carros do chantier selecionado (via clientes).
  const [chantierId, setChantierId] = useState<number | null>(null);
  const chantiersQuery = trpc.chantierPortal.listChantiers.useQuery(
    { companyId: effectiveCompanyId ?? 0 },
    { enabled: effectiveCompanyId != null },
  );
  const chantierList = chantiersQuery.data?.rows ?? [];
  const effectiveChantierId = chantierId ?? chantierList[0]?.id ?? null;
  const carsQuery = trpc.chantierPortal.carsOfChantier.useQuery(
    { companyId: effectiveCompanyId ?? 0, chantierId: effectiveChantierId ?? 0 },
    { enabled: effectiveCompanyId != null && effectiveChantierId != null },
  );
  // F-expertise-check (CEO 2026-09-22) — alarme "carros a finalizar": o
  // trabalho acabou mas o rapport de expertise não foi conferido com o valor
  // a faturar. Fica no topo, antes da busca: é a primeira coisa que o gerente
  // precisa ver ao abrir o portal.
  const finalizeQuery = trpc.chantierPortal.carsToFinalize.useQuery(
    { companyId: effectiveCompanyId ?? 0, chantierId: effectiveChantierId },
    { enabled: effectiveCompanyId != null },
  );
  // F-chantier-agenda — agenda do chantier selecionado (próximos 14 dias).
  const today = new Date();
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const agendaTo = new Date();
  agendaTo.setDate(agendaTo.getDate() + 14);
  const agendaQuery = trpc.chantierAgenda.listForManager.useQuery(
    {
      companyId: effectiveCompanyId ?? 0,
      chantierId: effectiveChantierId ?? 0,
      from: fmt(today),
      to: fmt(agendaTo),
    },
    { enabled: effectiveCompanyId != null && effectiveChantierId != null },
  );
  const [agendaOpen, setAgendaOpen] = useState(false);
  // F-operation-board — filtro por situação do carro no pátio do chantier.
  const [statusFiltro, setStatusFiltro] = useState<OperationStatus | null>(null);
  const opLabels = useOperationLabels();
  const [apDate, setApDate] = useState(fmt(today));
  const [apTime, setApTime] = useState("09:00");
  const [apPlate, setApPlate] = useState("");
  const [apLabel, setApLabel] = useState("");
  const utils = trpc.useUtils();
  const createApptMutation = trpc.chantierAgenda.createForManager.useMutation({
    onSuccess: () => {
      setApPlate("");
      setApLabel("");
      void utils.chantierAgenda.invalidate();
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  const setStatusMutation = trpc.chantierAgenda.setStatus.useMutation({
    onSuccess: () => void utils.chantierAgenda.invalidate(),
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  // Qual check acabou de ser gravado — o cartão correspondente se recolhe
  // sozinho e passa a mostrar o resumo com o ✓. Sem isso o formulário
  // continuava aberto com o botão "Registrar" ativo depois de salvar, e o
  // gerente não sabia se tinha dado certo (relato do CEO na recepção do
  // Tiguan). O `at` faz o cartão fechar também quando a recepção é reeditada.
  const [lastSaved, setLastSaved] = useState<{
    dossierId: number;
    kind: string;
    at: number;
  } | null>(null);
  const [saveError, setSaveError] = useState<{
    dossierId: number;
    kind: string;
    text: string;
  } | null>(null);
  const saveMutation = trpc.chantierPortal.saveCheck.useMutation({
    // Erro de uma tentativa anterior não pode ficar pendurado embaixo do
    // botão enquanto a nova tentativa roda.
    onMutate: () => setSaveError(null),
    onSuccess: (data, vars) => {
      const cls = (data as { customerClassified?: { carros: number } | null }).customerClassified;
      setFeedback({
        tone: "ok",
        text: cls
          ? `${t("chantier.saved")} ${t("chantier.customerClassifiedMsg")}`
          : t("chantier.saved"),
      });
      setSaveError(null);
      setLastSaved({ dossierId: vars.dossierId, kind: vars.kind, at: Date.now() });
      utils.chantierPortal.invalidate();
    },
    onError: (e, vars) => {
      setFeedback({ tone: "error", text: e.message });
      // O aviso do topo fica FORA DA TELA no celular — o gerente aperta
      // "Enregistrer" e nada parece acontecer. O erro também tem que
      // aparecer onde se aperta o botão (mesma lição da recepção do técnico).
      setSaveError({ dossierId: vars.dossierId, kind: vars.kind, text: e.message });
    },
  });
  // F-locale-uk — persiste a preferência de idioma escolhida no portal.
  const updateLocaleMutation = trpc.auth.updateLocale.useMutation();
  function handleLocaleChange(next: Locale) {
    updateLocaleMutation.mutate({ locale: next });
  }

  const logoutMutation = trpc.auth.logout.useMutation({
    onSettled: () => {
      queryClient.clear();
      window.location.assign("/login");
    },
  });

  const dossiers = searchQuery.data?.dossiers ?? [];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="sticky top-0 z-10 border-b border-neutral-800 bg-neutral-900/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold tracking-wide">
            <span className="text-lime">STORM</span> · {t("chantier.title")}
          </h1>
          <p className="text-[11px] text-neutral-500">{user.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* F-acesso-por-menu (CEO 2026-10-12) — gerente com menus do Gestion
              liberados: botão para ir ao Gestion (volta pelo menu lateral). */}
          {(user.menuGrants ?? []).length > 0 && (
            <Link
              href={ERP_MENU_PATHS[user.menuGrants[0]!.menu]}
              className="rounded-md border border-lime/50 bg-lime/10 px-2.5 py-1.5 text-xs font-semibold text-lime hover:bg-lime/20"
              data-testid="chantier-open-gestion"
            >
              {t("chantier.openGestion")} »
            </Link>
          )}
          {companies.length > 1 && (
            <select
              value={effectiveCompanyId ?? ""}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs"
            >
              {companies.map((c) => (
                <option key={c.companyId} value={c.companyId}>
                  {c.companyName}
                </option>
              ))}
            </select>
          )}
          {/* F-locale-uk — seletor de idioma também no portal do chantier. */}
          <LanguageSwitcher size="sm" onChange={handleLocaleChange} />
          <button
            onClick={() => logoutMutation.mutate()}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:border-red-400 hover:text-red-300"
          >
            {t("chantier.logout")}
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto p-4 space-y-5">
        {meQuery.data && !meQuery.data.isManager && (
          <p className="rounded-md bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-200">
            {t("chantier.notManagerNote")}
          </p>
        )}

        {/* F-tarefas (CEO 2026-10-29) — tarefas do escritório para o gerente
            (atrasadas, de hoje e dos próximos dias); some quando não há. */}
        {(tarefasQ.data?.rows.length ?? 0) > 0 && (
          <section className="space-y-2" data-testid="chantier-tasks">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-300">
              ✅ {t("tasks.title")}
            </h2>
            <MyTasksList variant="portal" showEmpty={false} />
          </section>
        )}

        <FinalizeAlarm
          rows={finalizeQuery.data?.rows ?? []}
          onPick={(placa) => {
            setMode("plate");
            setPlate(placa);
            setSearched({ value: placa, mode: "plate" });
            irParaBusca();
          }}
        />
        {/* CEO 2026-10-18 (Saint-Just, 19 carros): reparado SEM entrega
            registrada tem de gritar — sem o registro o carro não devia ser
            faturado e não entra no repasse do gerente. */}
        <DeliveryAlarm
          rows={(carsQuery.data?.rows ?? []).filter((c) => c.status === "reparado")}
          onPick={(c) => {
            const value = c.plate ?? c.vin ?? "";
            if (value.length < 4) return;
            setMode(c.plate ? "plate" : "vin");
            if (c.plate) setPlate(c.plate);
            else setVin(c.vin ?? "");
            setSearched({ value, mode: c.plate ? "plate" : "vin" });
            irParaBusca();
          }}
        />

        {/* Busca placa/VIN — mesmo formato do portal do técnico */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFeedback(null);
            const value =
              mode === "plate"
                ? formatFrenchPlate(plate.trim())
                : vin.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
            if (mode === "plate") setPlate(value);
            else setVin(value);
            setSearched(value.length >= 4 ? { value, mode } : null);
          }}
          ref={buscaRef}
          className="scroll-mt-20 space-y-3"
        >
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void handlePhoto(f);
            }}
          />
          {liveScan && effectiveCompanyId != null && (
            <CameraScanner
              modo={liveScan}
              companyId={effectiveCompanyId}
              chantierId={effectiveChantierId}
              onResult={(texto) => {
                const kind = liveScan === "vin" ? "vin" : "plate";
                setLiveScan(null);
                setMode(kind);
                setFeedback(null);
                if (kind === "plate") setPlate(texto);
                else setVin(texto);
                setSearched({ value: texto, mode: kind });
              }}
              onClose={() => setLiveScan(null)}
            />
          )}
          {scanning && (
            <p className="rounded-md border border-lime/30 bg-lime/10 px-3 py-2 text-center text-sm text-lime">
              📷 {t("chantier.scanning")}
            </p>
          )}
          <div
            onClick={() => setMode("plate")}
            className={`mx-auto flex w-full max-w-xs items-stretch overflow-hidden rounded-xl border-4 bg-white transition ${
              mode === "plate" ? "border-blue-600" : "border-neutral-700 opacity-50"
            }`}
          >
            <div className="flex w-9 flex-col items-center justify-center bg-blue-700 text-white">
              <span className="text-[9px] leading-none">★</span>
              <span className="text-lg font-bold leading-tight">F</span>
            </div>
            <input
              value={plate}
              onChange={(e) => {
                // F-scanner camada 0 — sem máscara a cada tecla: o "Escanear
                // Texto" do iOS cola o texto e a máscara o quebrava. Formata
                // no blur e no envio.
                const v = e.target.value.toUpperCase();
                setPlate(v);
                // F-busca-limpa (CEO 2026-09-21) — apagar a placa tira o carro
                // da tela. Antes o resultado ficava pendurado de uma busca
                // anterior, e não é isso que se espera ao limpar o campo.
                if (v.trim() === "") setSearched(null);
              }}
              onFocus={() => setMode("plate")}
              onBlur={() => setPlate((p) => formatFrenchPlate(p))}
              placeholder="AA-123-CD"
              maxLength={20}
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              autoFocus
              className="min-w-0 flex-1 bg-white px-2 py-3 text-center font-mono text-2xl font-semibold tracking-[0.15em] text-neutral-900 placeholder:text-neutral-300 outline-none"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openCamera("plate");
              }}
              disabled={scanning != null}
              aria-label="scan-plate"
              className="bg-blue-700 px-3 text-lg text-white disabled:opacity-50"
            >
              📷
            </button>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setMode((m) => (m === "plate" ? "vin" : "plate"))}
              aria-label="swap"
              className="rounded-lg border border-neutral-700 bg-neutral-900 px-4 py-1.5 text-lg hover:border-lime"
            >
              ⇄
            </button>
          </div>
          <div
            onClick={() => setMode("vin")}
            className={`rounded-lg border p-3 transition ${
              mode === "vin"
                ? "border-blue-500/60 bg-blue-500/10"
                : "border-neutral-800 bg-neutral-900/60 opacity-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="rounded bg-neutral-800 px-2 py-2 text-xs font-semibold text-neutral-300">
                VIN
              </span>
              <input
                value={vin}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();
                  setVin(v);
                  if (v.trim() === "") setSearched(null);
                }}
                onFocus={() => setMode("vin")}
                onBlur={() => setVin((v) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase())}
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                placeholder="WWWAUZ123456789"
                className="min-w-0 flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 font-mono text-sm uppercase tracking-wider outline-none focus:border-blue-500"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMode("vin");
                  openCamera("vin");
                }}
                disabled={scanning != null}
                aria-label="scan-vin"
                className="rounded-md border border-neutral-700 px-2.5 py-1.5 text-base disabled:opacity-50"
              >
                📷
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={(mode === "plate" ? plate : vin).trim().length < 4}
            className="w-full rounded-lg bg-lime px-5 py-3 text-sm font-semibold text-on-lime disabled:opacity-40"
          >
            🔍 {t("chantier.searchBtn")}
          </button>
        </form>

        {feedback && (
          <p
            className={`rounded-md px-4 py-3 text-sm border ${
              feedback.tone === "ok"
                ? "bg-lime/10 border-lime/30 text-lime"
                : feedback.tone === "warn"
                  ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
                  : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            {feedback.text}
          </p>
        )}

        {searchQuery.isFetching && (
          <p className="text-sm text-neutral-500">{t("chantier.searching")}</p>
        )}
        {searched != null && !searchQuery.isFetching && dossiers.length === 0 && (
          <p className="rounded-md bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm text-neutral-400">
            {t("chantier.noResults")}
          </p>
        )}

        {dossiers.map((d) => (
          <DossierCard
            key={d.id}
            dossier={d}
            companyId={effectiveCompanyId!}
            chantierId={effectiveChantierId}
            chantierName={chantierList.find((c) => c.id === effectiveChantierId)?.name ?? null}
            lastSaved={lastSaved}
            saveError={saveError}
            saving={saveMutation.isPending}
            onSave={(payload) =>
              saveMutation.mutate({
                companyId: effectiveCompanyId!,
                dossierId: d.id,
                ...payload,
              })
            }
          />
        ))}

        {/* F-chantier-manager-comm — repasse consolidado do responsável */}
        {effectiveCompanyId != null && <RepasseSection companyId={effectiveCompanyId} />}
        {/* CEO 2026-10-19 — totais dos clientes do chantier + faturas em atraso. */}
        {effectiveCompanyId != null && effectiveChantierId != null && (
          <ChantierInvoicesSection
            companyId={effectiveCompanyId}
            chantierId={effectiveChantierId}
          />
        )}

        {/* F-chantier-entity — carros do chantier (clientes vinculados) */}
        <section className="pt-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500">
              {t("chantier.carsTitle")}
            </h2>
            {chantierList.length > 0 && (
              <select
                value={effectiveChantierId ?? ""}
                onChange={(e) => setChantierId(Number(e.target.value))}
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs"
              >
                {chantierList.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.name}
                    {ch.city ? ` — ${ch.city}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          {chantierList.length === 0 && !chantiersQuery.isLoading && (
            <p className="text-sm text-neutral-600">{t("chantier.noChantiers")}</p>
          )}
          {effectiveChantierId != null && carsQuery.data?.rows.length === 0 && (
            <p className="text-sm text-neutral-600">{t("chantier.carsEmpty")}</p>
          )}
          {/* F-operation-board — onde está cada carro do pátio. Clicar filtra. */}
          {carsQuery.data && carsQuery.data.rows.length > 0 && (
            <div className="mb-2 grid grid-cols-4 gap-1.5">
              {(["a_reparar", "em_reparacao", "reparado", "entregue"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFiltro((cur) => (cur === s ? null : s))}
                  className={`rounded-lg px-2 py-1.5 text-center transition ${OPERATION_TONE[s]} ${
                    statusFiltro === s ? "ring-2 ring-current" : "opacity-90"
                  }`}
                >
                  <span className="block text-base font-bold leading-none tabular-nums">
                    {carsQuery.data.counts[s]}
                  </span>
                  <span className="block text-[10px] leading-tight">{opLabels[s]}</span>
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(carsQuery.data?.rows ?? [])
              .filter((c) => (statusFiltro ? c.status === statusFiltro : true))
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    const value = c.plate ?? c.vin ?? "";
                    if (value.length >= 4) {
                      setMode(c.plate ? "plate" : "vin");
                      if (c.plate) setPlate(c.plate);
                      else setVin(c.vin ?? "");
                      setSearched({ value, mode: c.plate ? "plate" : "vin" });
                      irParaBusca();
                    }
                  }}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-left text-sm hover:border-lime/50"
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <CarLogo make={c.make} size={18} />
                    <span className="min-w-0 truncate">
                      <span className="font-mono">{c.plate ?? c.vin ?? c.dossierCode}</span>
                      <span className="text-neutral-500"> · {c.customerName}</span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {c.franchise && (
                      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
                        {t("chantier.franchiseLabel")}
                      </span>
                    )}
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        c.received ? "bg-lime/15 text-lime" : "bg-neutral-800 text-neutral-400"
                      }`}
                    >
                      📥
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${OPERATION_TONE[c.status]}`}
                    >
                      {opLabels[c.status]}
                    </span>
                  </span>
                </button>
              ))}
          </div>
        </section>

        {/* F-chantier-agenda — agenda do chantier (gerente administra) */}
        {effectiveChantierId != null && (
          <section className="pt-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-xs uppercase tracking-wider text-neutral-500">
                {t("chantier.agendaTitle")}
              </h2>
              <button
                onClick={() => setAgendaOpen((o) => !o)}
                className="rounded-md border border-neutral-700 px-3 py-1 text-xs hover:border-lime"
              >
                {t("chantier.agendaAddBtn")}
              </button>
            </div>
            {agendaOpen && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createApptMutation.mutate({
                    companyId: effectiveCompanyId!,
                    chantierId: effectiveChantierId,
                    date: apDate,
                    time: apTime,
                    plate: apPlate.trim() || undefined,
                    vehicleLabel: apLabel.trim() || undefined,
                  });
                }}
                className="mb-3 space-y-2 rounded-lg border border-neutral-800 bg-neutral-900 p-3"
              >
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={apDate}
                    onChange={(e) => setApDate(e.target.value)}
                    required
                    className="rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                  />
                  <input
                    type="time"
                    value={apTime}
                    onChange={(e) => setApTime(e.target.value)}
                    required
                    className="rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={apPlate}
                    onChange={(e) => setApPlate(e.target.value.toUpperCase())}
                    onBlur={() => setApPlate((p) => formatFrenchPlate(p))}
                    placeholder="AA-123-CD"
                    maxLength={20}
                    inputMode="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck={false}
                    className="rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-center font-mono text-sm outline-none focus:border-lime"
                  />
                  <input
                    value={apLabel}
                    onChange={(e) => setApLabel(e.target.value)}
                    placeholder="Renault Clio…"
                    maxLength={120}
                    className="rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                  />
                </div>
                <button
                  type="submit"
                  disabled={createApptMutation.isPending}
                  className="w-full rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-on-lime disabled:opacity-40"
                >
                  {t("chantier.agendaSaveBtn")}
                </button>
              </form>
            )}
            {(agendaQuery.data?.rows ?? []).length === 0 && (
              <p className="text-sm text-neutral-600">{t("chantier.agendaEmpty")}</p>
            )}
            <div className="space-y-1.5">
              {(agendaQuery.data?.rows ?? []).map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-sm"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-mono">{a.date}</span>
                    <span className="font-semibold text-lime"> {a.time}</span>
                    {a.plate && <span className="font-mono"> · {a.plate}</span>}
                    {a.vehicleLabel && (
                      <span className="text-neutral-400"> · {a.vehicleLabel}</span>
                    )}
                    {a.customerName && (
                      <span className="text-neutral-500"> · {a.customerName}</span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        a.status === "scheduled"
                          ? "bg-sky-500/15 text-sky-300"
                          : a.status === "arrived"
                            ? "bg-amber-500/15 text-amber-300"
                            : "bg-lime/15 text-lime"
                      }`}
                    >
                      {a.status === "scheduled"
                        ? t("chantier.statusScheduled")
                        : a.status === "arrived"
                          ? t("chantier.statusArrived")
                          : t("chantier.statusDone")}
                    </span>
                    {a.status === "scheduled" && (
                      <button
                        onClick={() =>
                          setStatusMutation.mutate({
                            companyId: effectiveCompanyId!,
                            appointmentId: a.id,
                            status: "arrived",
                          })
                        }
                        className="rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] hover:border-lime"
                      >
                        {t("chantier.agendaArrivedBtn")}
                      </button>
                    )}
                    {a.status === "arrived" && (
                      <button
                        onClick={() =>
                          setStatusMutation.mutate({
                            companyId: effectiveCompanyId!,
                            appointmentId: a.id,
                            status: "done",
                          })
                        }
                        className="rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] hover:border-lime"
                      >
                        {t("chantier.agendaDoneBtn")}
                      </button>
                    )}
                    {a.status !== "done" && (
                      <button
                        onClick={() =>
                          setStatusMutation.mutate({
                            companyId: effectiveCompanyId!,
                            appointmentId: a.id,
                            status: "cancelled",
                          })
                        }
                        className="rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] hover:border-red-400 hover:text-red-300"
                      >
                        ✕
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      <TeamNoticesPopup
        notices={recados}
        tasks={tarefasPopup}
        tasksHref={null}
        open={recadosAbertos}
        onClose={() => setRecadosAbertos(false)}
      />
    </div>
  );
}

type FoundDossier = {
  id: number;
  dossierCode: string;
  plate: string | null;
  vin: string | null;
  vehicleMakeSnapshot: string | null;
  vehicleModelSnapshot: string | null;
  externalDevisRef: string | null;
  devisDate: string | null;
  devisBreakdown: unknown;
  endCustomerName: string | null;
  customerName: string;
  customerKind: string;
  customerRetentionPct: string | null;
  /** CEO 2026-11-04 — cliente "a revisar" ou Pro sem remise: a recepção classifica. */
  customerNeedsClassification: boolean;
  /** F-cliente-sem-chantier — null = cliente ainda não ligado a chantier nenhum. */
  customerChantierId: number | null;
  franchiseRequired: boolean;
  /** F-seguro-contato — contatos do sinistro (seguradora / peritagem). */
  insurerName: string | null;
  insurerEmail: string | null;
  expertName: string | null;
  expertEmail: string | null;
  claimRef: string | null;
  operationStatus: "a_reparar" | "em_reparacao" | "reparado" | "entregue";
  /** F-expertise-check — conferência rapport × valor a faturar. */
  expertise: ExpertiseState | null;
  /** F-recepcao-status — o que falta para o carro ser liberado ao técnico. */
  receptionMissing: Array<{ code: string; count?: number }>;
  receptionReleased: boolean;
  technicians: Array<{
    serviceType: string;
    technicianName: string;
    demontageDone: boolean;
    montageDone: boolean;
    dspDone: boolean;
    demontageBy: string | null;
    montageBy: string | null;
  }>;
  checks: Array<{
    kind: string;
    damages: string[];
    notes: string | null;
    franchise: boolean;
    franchiseAmount: string | null;
    mileageKm: number | null;
    loanerVehicle: boolean | null;
    loanerVehicleInfo: string | null;
    pareBriseOk: boolean | null;
    cielOk: boolean | null;
    signed: boolean;
    workOk: boolean | null;
    updatedAt: string | Date;
  }>;
};

type SavePayload = {
  kind: "reception" | "delivery";
  damages: string[];
  notes?: string;
  franchise: boolean;
  franchiseAmount?: number | null;
  mileageKm?: number | null;
  loanerVehicle?: boolean | null;
  loanerVehicleInfo?: string | null;
  pareBriseOk?: boolean | null;
  cielOk?: boolean | null;
  signatureBase64?: string | null;
  signedByName?: string | null;
  workOk?: boolean | null;
  /** F-cliente-sem-chantier — resposta do gerente à pergunta do chantier. */
  chantierId?: number;
  attachCustomerToChantier?: boolean;
  /** CEO 2026-11-04 — o gerente classifica o cliente na recepção. */
  customerKind?: "individual" | "business";
  customerRemisePct?: number | null;
};

function DossierCard({
  dossier: d,
  companyId,
  chantierId,
  chantierName,
  lastSaved,
  saveError,
  saving,
  onSave,
}: {
  dossier: FoundDossier;
  companyId: number;
  /** F-cliente-sem-chantier — chantier selecionado pelo gerente. */
  chantierId: number | null;
  chantierName: string | null;
  lastSaved: { dossierId: number; kind: string; at: number } | null;
  saveError: { dossierId: number; kind: string; text: string } | null;
  saving: boolean;
  onSave: (p: SavePayload) => void;
}) {
  const savedAtFor = (kind: string): number | null =>
    lastSaved && lastSaved.dossierId === d.id && lastSaved.kind === kind ? lastSaved.at : null;
  const errorFor = (kind: string): string | null =>
    saveError && saveError.dossierId === d.id && saveError.kind === kind ? saveError.text : null;
  const t = useT();
  const { locale } = useI18n();
  const [fotosAbertas, setFotosAbertas] = useState(false);
  const bd = (d.devisBreakdown ?? null) as DevisBd | null;
  const methodTone = (m: string) =>
    m.toUpperCase() === "DSP"
      ? "bg-lime/15 text-lime"
      : m.toUpperCase() === "DAP"
        ? "bg-amber-500/15 text-amber-300"
        : /ECHANGE|REMPLACEMENT/i.test(m)
          ? "bg-red-500/15 text-red-300"
          : "bg-blue-500/15 text-blue-300";
  // F-carro-pronto (CEO 2026-09-21) — o gerente abre a placa e vê de cara se
  // ainda falta algo. O estado vem do backend (mesma regra do quadro do
  // pátio), para o card e o quadro nunca discordarem sobre o mesmo carro.
  const carroPronto = d.operationStatus === "reparado" || d.operationStatus === "entregue";
  return (
    <div
      className={`rounded-xl border bg-neutral-900 p-4 space-y-3 ${
        carroPronto ? "border-lime/40" : "border-neutral-800"
      }`}
    >
      {carroPronto && (
        <div className="rounded-lg border border-lime/40 bg-lime/10 px-3 py-2">
          <p className="text-sm font-semibold text-lime">✓ {t("chantier.carRepairedTitle")}</p>
          <p className="text-[11px] text-neutral-300">{t("chantier.carRepairedHint")}</p>
        </div>
      )}

      {/* F-recepcao-status (CEO 2026-09-21) — "o operador não consegue
          identificar qual o próximo passo e o técnico continua esperando".
          Enquanto a recepção não estiver COMPLETA o técnico esbarra na trava
          do reparo; o card dizia só "✓ feito" e não contava isso a ninguém. */}
      <ProximoPasso d={d} jaPronto={carroPronto} />

      {d.operationStatus === "a_reparar" &&
        d.receptionMissing.length > 0 &&
        !d.receptionMissing.some((m) => m.code === "recepcao") && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs font-semibold text-amber-200">
            ⚠ {t("chantier.recMissingPre")} {listaDeFaltas(d.receptionMissing, t)}
          </p>
        )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-lg font-bold tracking-wider">{d.plate ?? d.vin ?? "—"}</p>
          <p className="flex items-center gap-1.5 text-sm text-neutral-300">
            <CarLogo make={d.vehicleMakeSnapshot} size={18} />
            {[d.vehicleMakeSnapshot, d.vehicleModelSnapshot].filter(Boolean).join(" ") || "—"}
          </p>
          <p className="text-xs text-neutral-500">
            {d.endCustomerName ?? d.customerName}
            {d.externalDevisRef && ` · ${d.externalDevisRef}`}
            {d.devisDate && ` · ${d.devisDate}`}
          </p>
        </div>
      </div>

      {/* F-servicos-gerente (CEO 2026-09-21) — quem fez o quê, no MESMO card,
          com o estado de cada metade do dégarnissage. A linha antiga só dizia
          "Montagem: fulano", o que dava a entender que ele tinha feito as duas
          metades mesmo quando outro técnico assumiu uma delas. Sem valores:
          quanto cada técnico ganha não é assunto do gerente do chantier. */}
      {/* CEO 2026-10-19 (ET-628-AM / GC-824-MN): o gerente entregou carros que
          nenhum técnico tinha assumido — fecharam "sem técnico" e sumiram da
          lista do técnico. Avisa antes de registrar a entrega. */}
      {d.technicians.length === 0 && (
        <p
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-200"
          data-testid="chantier-no-tech-warning"
        >
          ⚠ {t("chantier.noTechWarning")}
        </p>
      )}
      {d.technicians.length > 0 && (
        <div className="rounded border border-neutral-800 bg-neutral-950/60 px-2.5 py-2 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-neutral-600">
            {t("chantier.servicesTitle")}
          </p>
          {d.technicians.map((l) => (
            <p key={l.serviceType} className="text-xs text-neutral-300">
              {l.serviceType === "dsp" ? (
                <>
                  <span className="text-neutral-500">{t("chantier.servicePdrLabel")}: </span>
                  {l.technicianName}
                  <span className={l.dspDone ? "text-lime" : "text-neutral-600"}>
                    {" "}
                    {l.dspDone ? "✓" : "○"}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-neutral-500">{t("chantier.serviceDemontage")}: </span>
                  {l.demontageBy ?? l.technicianName}
                  <span className={l.demontageDone ? "text-lime" : "text-neutral-600"}>
                    {" "}
                    {l.demontageDone ? "✓" : "○"}
                  </span>
                  <span className="block">
                    <span className="text-neutral-500">{t("chantier.serviceMontage")}: </span>
                    {l.montageBy ?? l.technicianName}
                    <span className={l.montageDone ? "text-lime" : "text-neutral-600"}>
                      {" "}
                      {l.montageDone ? "✓" : "○"}
                    </span>
                  </span>
                </>
              )}
            </p>
          ))}
          <button
            type="button"
            onClick={() => setFotosAbertas((v) => !v)}
            className="mt-1 rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] text-neutral-300 hover:border-lime hover:text-lime"
          >
            📷 {fotosAbertas ? t("chantier.hidePhotosBtn") : t("chantier.showPhotosBtn")}
          </button>
          {fotosAbertas && <FotosDoReparo companyId={companyId} dossierId={d.id} />}
        </div>
      )}

      {/* Devis COMPLETO (sem comissões — decisão do CEO) */}
      {bd && (
        <div className="rounded-lg border border-neutral-800 bg-neutral-950/70 p-3 space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider text-neutral-500">
            {t("chantier.devisTitle")}
            {bd.impactsTotal != null && ` · ${bd.impactsTotal} ${t("chantier.impactsWord")}`}
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
            {(
              [
                ["Total DSP", bd.totalDsp],
                ["Dégarnissage", bd.degarnissage],
                ["Carrosserie", bd.carrosserie],
                [t("chantier.devisPieces"), bd.pieces],
                [t("chantier.devisPareBrise"), bd.pareBrise],
                ["TVA", bd.tva],
              ] as const
            )
              .filter(([, v]) => Number(v ?? 0) > 0)
              .map(([label, v]) => (
                <div key={label} className="flex justify-between gap-2">
                  <span className="text-neutral-500">{label}</span>
                  <span className="font-mono">{formatMoney(Number(v), { decimals: 2 })}</span>
                </div>
              ))}
            {Number(bd.grandTotalHt ?? 0) > 0 && (
              <div className="flex justify-between gap-2">
                <span className="text-neutral-400 font-semibold">Total HT</span>
                <span className="font-mono font-semibold">
                  {formatMoney(Number(bd.grandTotalHt), { decimals: 2 })}
                </span>
              </div>
            )}
            {Number(bd.totalTtc ?? 0) > 0 && (
              <div className="flex justify-between gap-2">
                <span className="text-neutral-400 font-semibold">Total TTC</span>
                <span className="font-mono font-semibold">
                  {formatMoney(Number(bd.totalTtc), { decimals: 2 })}
                </span>
              </div>
            )}
          </div>
          {!!bd.panelLines?.length && (
            <div className="border-t border-neutral-800 pt-1.5 space-y-1">
              {bd.panelLines.map((pl, i) => (
                <div key={i} className="flex items-start justify-between gap-2 text-sm">
                  <span className="min-w-0">
                    {trPanel(pl.panel, locale)}
                    {pl.note && (
                      <span className="block text-[11px] text-amber-300">
                        ⚠ {trNote(pl.note, locale)}
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {pl.impacts != null && (
                      <span className="font-mono text-xs text-neutral-400">{pl.impacts}×</span>
                    )}
                    {pl.sizeClass && (
                      <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] text-neutral-300">
                        {pl.sizeClass}
                      </span>
                    )}
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${methodTone(pl.method)}`}
                    >
                      {trMethod(pl.method, locale)}
                    </span>
                  </span>
                </div>
              ))}
              {bd.deposeRepose && (
                <p className="text-xs text-neutral-400">
                  {t("chantier.deposeReposeLabel")}: {trDepose(bd.deposeRepose, locale)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* F-chantier-manager-comm — remise do cadastro conferida pelo RESPONSÁVEL
          na recepção (o técnico nunca vê; aqui é o papel dele conferir). */}
      {d.customerRetentionPct != null && Number(d.customerRetentionPct) > 0 && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs font-semibold text-amber-200">
          🏷 {t("chantier.remiseCheckPre")} {Number(d.customerRetentionPct).toFixed(0)}%{" "}
          {t("chantier.remiseCheckSuf")}
        </p>
      )}

      <CheckForm
        kind="reception"
        companyId={companyId}
        dossierId={d.id}
        saveError={errorFor("reception")}
        incomplete={!d.receptionReleased && d.operationStatus === "a_reparar"}
        franchiseRequired={d.franchiseRequired}
        customerName={d.customerName}
        classificarCliente={d.customerNeedsClassification}
        clienteRemiseAtual={d.customerRetentionPct}
        customerWithoutChantier={d.customerChantierId == null}
        chantierId={chantierId}
        chantierName={chantierName}
        existing={d.checks.find((c) => c.kind === "reception") ?? null}
        savedAt={savedAtFor("reception")}
        saving={saving}
        onSave={onSave}
      />
      <ContatosSinistro
        companyId={companyId}
        dossier={d}
        exigido={d.customerKind === "individual"}
      />
      <ReceptionDocs
        companyId={companyId}
        dossierId={d.id}
        exigirExpertise={d.expertise?.pending === true}
      />
      {d.expertise && (
        <ExpertiseBlock
          companyId={companyId}
          dossierId={d.id}
          e={d.expertise}
          finished={carroPronto}
        />
      )}
      <CheckForm
        kind="delivery"
        companyId={companyId}
        dossierId={d.id}
        saveError={errorFor("delivery")}
        franchiseRequired={false}
        existing={d.checks.find((c) => c.kind === "delivery") ?? null}
        savedAt={savedAtFor("delivery")}
        saving={saving}
        onSave={onSave}
      />
    </div>
  );
}

/** Faltas da recepção em texto curto ("3 fotos · assinatura"). */
function listaDeFaltas(
  faltas: Array<{ code: string; count?: number }>,
  t: ReturnType<typeof useT>,
): string {
  const rotulo: Record<string, string> = {
    para_brisa: t("chantier.recMissPareBrise"),
    ciel: t("chantier.recMissCiel"),
    assinatura: t("chantier.recMissSignature"),
    foto_para_brisa: t("chantier.recMissPhotoPareBrise"),
    foto_ciel: t("chantier.recMissPhotoCiel"),
  };
  return faltas
    .map((m) =>
      m.code === "fotos"
        ? `${m.count} ${(m.count ?? 0) === 1 ? t("chantier.recMissPhoto") : t("chantier.recMissPhotos")}`
        : (rotulo[m.code] ?? m.code),
    )
    .join(" · ");
}

/**
 * Situação do carro em uma linha, do ponto de vista de QUEM OPERA: o que já
 * aconteceu e qual é o próximo passo. A recepção incompleta é o caso que mais
 * confunde — o carro parece recebido e o técnico não consegue assumir.
 */
function ProximoPasso({ d, jaPronto }: { d: FoundDossier; jaPronto: boolean }) {
  const t = useT();
  // Carro entregue: o banner verde acima já diz tudo — repetir "liberado para
  // o reparo" num carro que saiu do pátio só atrapalha a leitura.
  if (jaPronto) {
    if (d.operationStatus === "entregue") return null;
    return (
      <p className="text-[11px] text-neutral-400">
        {t("chantier.stepNextPre")} {t("chantier.stepDoDelivery")}
      </p>
    );
  }
  // Carro que já está em reparo (ou pronto) passou pela trava, ou é histórico
  // importado que ela não alcança: cobrar recepção aí seria ruído.
  if (!d.receptionReleased && d.operationStatus === "a_reparar") {
    const naoRecebido = d.receptionMissing.some((m) => m.code === "recepcao");
    return (
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2">
        <p className="text-sm font-semibold text-amber-200">
          ⚠ {naoRecebido ? t("chantier.stepNotReceived") : t("chantier.stepReceptionIncomplete")}
        </p>
        <p className="text-[11px] text-neutral-300">{t("chantier.stepBlockedHint")}</p>
      </div>
    );
  }
  const texto =
    d.operationStatus === "entregue"
      ? t("chantier.stepDelivered")
      : d.operationStatus === "reparado"
        ? t("chantier.stepDoDelivery")
        : d.operationStatus === "em_reparacao"
          ? t("chantier.stepInRepair")
          : t("chantier.stepWaitingTechnician");
  return (
    <div className="rounded-lg border border-lime/30 bg-lime/5 px-3 py-2">
      <p className="text-sm font-semibold text-lime">✓ {t("chantier.stepReleased")}</p>
      <p className="text-[11px] text-neutral-300">
        {t("chantier.stepNextPre")} {texto}
      </p>
    </div>
  );
}

/**
 * Fotos do reparo — as mesmas que o técnico tirou, dentro do card do carro.
 * Abrir em outra tela obrigaria o gerente a perder o contexto do veículo.
 */
function FotosDoReparo({ companyId, dossierId }: { companyId: number; dossierId: number }) {
  const t = useT();
  const etapas = [
    { kind: "reception" as const, rotulo: t("chantier.photosReception") },
    { kind: "montage" as const, rotulo: t("chantier.photosDemontage") },
    { kind: "montage_end" as const, rotulo: t("chantier.photosMontageEnd") },
    { kind: "delivery" as const, rotulo: t("chantier.photosDelivery") },
  ];
  return (
    <div className="mt-1.5 space-y-2">
      {etapas.map((e) => (
        <EtapaDeFotos key={e.kind} companyId={companyId} dossierId={dossierId} {...e} />
      ))}
    </div>
  );
}

function EtapaDeFotos({
  companyId,
  dossierId,
  kind,
  rotulo,
}: {
  companyId: number;
  dossierId: number;
  kind: "reception" | "montage" | "montage_end" | "delivery";
  rotulo: string;
}) {
  const q = trpc.chantierPortal.listChecklistPhotos.useQuery({
    companyId,
    dossierId,
    checkKind: kind,
  });
  const fotos = q.data?.rows ?? [];
  if (fotos.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-neutral-600">
        {rotulo} · {fotos.length}
      </p>
      <div className="mt-1 flex flex-wrap gap-1">
        {fotos.map((f) => (
          <a key={f.url} href={f.url} target="_blank" rel="noreferrer">
            <img
              src={f.url}
              alt={f.kind}
              className="h-12 w-12 rounded border border-neutral-700 object-cover"
            />
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * F-seguro-contato (CEO 2026-09-22) — quem mais recebe a fatura deste carro.
 *
 * "O gerente deve registrar o contato da seguradora para todos os clientes
 * particulares, pois ao finalizar o trabalho deve enviar a fatura tanto ao
 * cliente quanto à seguradora ou ao gabinete de peritagem. Isso é
 * fundamental para que não haja atrasos nos recebimentos."
 *
 * Fica na recepção porque é ali que o cliente está na frente do gerente, com
 * o papel do sinistro na mão — depois vira caçada por telefone.
 */
function ContatosSinistro({
  companyId,
  dossier: d,
  exigido,
}: {
  companyId: number;
  dossier: FoundDossier;
  exigido: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [aberto, setAberto] = useState(false);
  const [insurerName, setInsurerName] = useState(d.insurerName ?? "");
  const [insurerEmail, setInsurerEmail] = useState(d.insurerEmail ?? "");
  const [expertName, setExpertName] = useState(d.expertName ?? "");
  const [expertEmail, setExpertEmail] = useState(d.expertEmail ?? "");
  const [claimRef, setClaimRef] = useState(d.claimRef ?? "");
  const [erro, setErro] = useState<string | null>(null);

  const mut = trpc.chantierPortal.saveInsuranceContacts.useMutation({
    onSuccess: () => {
      setErro(null);
      setAberto(false);
      void utils.chantierPortal.findByPlate.invalidate();
    },
    onError: (e) => setErro(e.message),
  });

  const temAlgum = d.insurerEmail != null || d.expertEmail != null;
  const faltando = exigido && !temAlgum;
  const campos: Array<[string, string, (v: string) => void, string]> = [
    [t("chantier.insurerNameLabel"), insurerName, setInsurerName, "text"],
    [t("chantier.insurerEmailLabel"), insurerEmail, setInsurerEmail, "email"],
    [t("chantier.expertNameLabel"), expertName, setExpertName, "text"],
    [t("chantier.expertEmailLabel"), expertEmail, setExpertEmail, "email"],
    [t("chantier.claimRefLabel"), claimRef, setClaimRef, "text"],
  ];

  return (
    <div
      className={`rounded-lg border p-3 space-y-2 ${
        faltando ? "border-amber-500/50 bg-amber-500/10" : "border-neutral-800 bg-neutral-950/60"
      }`}
    >
      <button
        type="button"
        onClick={() => setAberto((o) => !o)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="text-sm font-semibold">
          🛡 {t("chantier.insuranceTitle")}
          {faltando && (
            <span className="ml-2 text-xs text-amber-300">⚠ {t("chantier.insuranceMissing")}</span>
          )}
          {temAlgum && <span className="ml-2 text-xs text-lime">✓</span>}
        </span>
        <span className="text-neutral-500">{aberto ? "▲" : "▼"}</span>
      </button>

      {!aberto && temAlgum && (
        <p className="text-xs text-neutral-400">
          {[d.insurerName ?? d.insurerEmail, d.expertName ?? d.expertEmail]
            .filter(Boolean)
            .join(" · ")}
          {d.claimRef ? ` · ${d.claimRef}` : ""}
        </p>
      )}
      {!aberto && faltando && (
        <p className="text-[11px] text-amber-200">{t("chantier.insuranceHint")}</p>
      )}

      {aberto && (
        <div className="space-y-2">
          <p className="text-[11px] text-neutral-400">{t("chantier.insuranceHint")}</p>
          {campos.map(([label, valor, set, tipo]) => (
            <label key={label} className="block">
              <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
                {label}
              </span>
              <input
                type={tipo}
                value={valor}
                onChange={(e) => set(e.target.value)}
                className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
              />
            </label>
          ))}
          {erro && <p className="text-xs text-red-300">{erro}</p>}
          <button
            type="button"
            disabled={mut.isPending}
            onClick={() =>
              mut.mutate({
                companyId,
                dossierId: d.id,
                insurerName,
                insurerEmail,
                expertName,
                expertEmail,
                claimRef,
              })
            }
            className="w-full rounded-lg bg-lime px-4 py-2.5 text-sm font-semibold text-on-lime disabled:opacity-40"
          >
            {mut.isPending ? t("chantier.savingBtn") : t("chantier.saveBtn")}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Documentos da recepção: expertise, carte grise, CNH do proprietário.
 *
 * F-docs-recepcao (Corsa FS-374-MW, CEO 2026-09-21) — o gerente mandou os três
 * documentos e a tela continuou pedindo os três: só o primeiro chegou ao
 * servidor. Três defeitos no mesmo lugar:
 *   1. um upload em andamento DESABILITAVA os outros botões — tocar em "Carte
 *      grise" enquanto a expertise subia não abria nada e não avisava nada;
 *   2. nenhum `onError`: falha de upload morria calada;
 *   3. o botão nunca mudava de estado, então nem o que subiu parecia ter
 *      subido.
 * Agora cada categoria mostra ✓ com o arquivo, o erro aparece, e a foto é
 * comprimida no aparelho (a do Corsa foi 2,4 MB crus) como no checklist.
 */
function ReceptionDocs({
  companyId,
  dossierId,
  exigirExpertise,
}: {
  companyId: number;
  dossierId: number;
  exigirExpertise?: boolean;
}) {
  const utils = trpc.useUtils();
  const [enviando, setEnviando] = useState<DocCat | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const docsQuery = trpc.chantierPortal.listReceptionDocs.useQuery({ companyId, dossierId });
  const uploadMut = trpc.chantierPortal.uploadReceptionDoc.useMutation({
    onSuccess: () => {
      setEnviando(null);
      void utils.chantierPortal.listReceptionDocs.invalidate();
      // O card da conferência lê "rapport anexado?" do backend — sem isto o
      // botão de confirmar continuava travado com o documento já no servidor.
      void utils.chantierPortal.findByPlate.invalidate();
      void utils.chantierPortal.carsToFinalize.invalidate();
    },
    onError: (e) => {
      setEnviando(null);
      setErro(e.message);
    },
  });

  return (
    <ReceptionDocsCard
      docs={docsQuery.data?.rows ?? []}
      sending={enviando}
      error={erro}
      requiredCats={exigirExpertise ? ["expertise"] : []}
      onPick={(cat, fileName, contentBase64) => {
        setErro(null);
        setEnviando(cat);
        uploadMut.mutate({ companyId, dossierId, category: cat, fileName, contentBase64 });
      }}
    />
  );
}

/** F-expertise-check — conferência rapport × fatura no card do carro. */
function ExpertiseBlock({
  companyId,
  dossierId,
  e,
  finished,
}: {
  companyId: number;
  dossierId: number;
  e: ExpertiseState;
  finished: boolean;
}) {
  const utils = trpc.useUtils();
  const [erro, setErro] = useState<string | null>(null);
  const mut = trpc.chantierPortal.saveExpertiseCheck.useMutation({
    onSuccess: () => {
      setErro(null);
      void utils.chantierPortal.findByPlate.invalidate();
      void utils.chantierPortal.carsToFinalize.invalidate();
    },
    onError: (err) => setErro(err.message),
  });
  return (
    <ExpertiseCheckCard
      expertise={e}
      finished={finished}
      saving={mut.isPending}
      error={erro}
      onConfirm={(p) => {
        setErro(null);
        mut.mutate({ companyId, dossierId, ...p });
      }}
    />
  );
}

/**
 * Alarme "carros a finalizar" — o trabalho acabou e o rapport de expertise
 * ainda não foi conferido com o valor a faturar. Toca na placa e a busca
 * abre o carro: sem isso o gerente teria que decorar a placa e digitar.
 */
function FinalizeAlarm({
  rows,
  onPick,
}: {
  rows: Array<{
    dossierId: number;
    plate: string | null;
    vin: string | null;
    customerName: string | null;
    hasDoc: boolean;
  }>;
  onPick: (placa: string) => void;
}) {
  const t = useT();
  if (rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 space-y-1.5">
      <p className="text-sm font-bold text-amber-200">
        ⚠ {t("chantier.finalizeAlarmTitle")} · {rows.length}
      </p>
      <p className="text-[11px] text-neutral-300">{t("chantier.finalizeAlarmHint")}</p>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((r) => (
          <button
            key={r.dossierId}
            type="button"
            onClick={() => r.plate && onPick(r.plate)}
            className="rounded-md border border-amber-500/50 px-2 py-1 text-left text-[11px] hover:border-amber-300"
          >
            <span className="font-mono font-semibold text-amber-100">
              {r.plate ?? r.vin ?? `#${r.dossierId}`}
            </span>
            {!r.hasDoc && (
              <span className="ml-1 text-amber-300">· {t("chantier.finalizeNoDoc")}</span>
            )}
            {r.customerName && (
              <span className="block truncate text-neutral-400">{r.customerName}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * CEO 2026-10-18 — carros REPARADOS sem entrega registrada (quadro da
 * operação, status "reparado"): o gerente toca na placa, registra a entrega
 * e o controle; só então o carro é faturado e conta no repasse dele.
 */
function DeliveryAlarm({
  rows,
  onPick,
}: {
  rows: Array<{
    id: number;
    plate: string | null;
    vin: string | null;
    customerName: string | null;
    invoiced: boolean;
  }>;
  onPick: (car: { plate: string | null; vin: string | null }) => void;
}) {
  const t = useT();
  if (rows.length === 0) return null;
  return (
    <div
      className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 space-y-1.5"
      data-testid="delivery-alarm"
    >
      <p className="text-sm font-bold text-red-200">
        🚚 {t("chantier.deliveryAlarmTitle")} · {rows.length}
      </p>
      <p className="text-[11px] text-neutral-300">{t("chantier.deliveryAlarmHint")}</p>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onPick(r)}
            className="rounded-md border border-red-500/50 px-2 py-1 text-left text-[11px] hover:border-red-300"
          >
            <span className="font-mono font-semibold text-red-100">
              {r.plate ?? r.vin ?? `#${r.id}`}
            </span>
            {r.invoiced && (
              <span className="ml-1 text-amber-300">· {t("chantier.deliveryAlarmInvoiced")}</span>
            )}
            {r.customerName && (
              <span className="block truncate text-neutral-400">{r.customerName}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * CEO 2026-10-19 — faturas dos clientes do chantier: totais por cliente
 * (faturado, recebido, em aberto, em atraso) e a lista das faturas em atraso,
 * para o gerente cobrar no local. Valores HT; nenhuma comissão.
 */
function ChantierInvoicesSection({
  companyId,
  chantierId,
}: {
  companyId: number;
  chantierId: number;
}) {
  const t = useT();
  const q = trpc.chantierPortal.chantierInvoices.useQuery({ companyId, chantierId });
  const customers = q.data?.customers ?? [];
  const overdue = q.data?.overdue ?? [];
  const totals = q.data?.totals;
  const m = (n: number) => formatMoney(n, { decimals: 2 });
  return (
    <section className="pt-2" data-testid="chantier-invoices">
      <h2 className="mb-1 text-xs uppercase tracking-wider text-neutral-500">
        🧾 {t("chantier.invoicesTitle")}
      </h2>
      <p className="mb-2 text-[11px] text-neutral-500">{t("chantier.invoicesHint")}</p>
      {q.isLoading ? (
        <p className="text-sm text-neutral-600">…</p>
      ) : customers.length === 0 ? (
        <p className="text-sm text-neutral-600">{t("chantier.invoicesEmpty")}</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(
              [
                [t("chantier.invColTotal"), totals?.total ?? 0, "text-neutral-100"],
                [t("chantier.invColReceived"), totals?.received ?? 0, "text-lime"],
                [t("chantier.invColOpen"), totals?.open ?? 0, "text-amber-300"],
                [t("chantier.invColOverdue"), totals?.overdue ?? 0, "text-red-300"],
              ] as const
            ).map(([label, value, cls]) => (
              <div
                key={label}
                className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2"
              >
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p>
                <p className={`font-mono text-sm font-semibold ${cls}`}>{m(value)}</p>
              </div>
            ))}
          </div>
          <div className="space-y-1">
            {customers.map((c) => (
              <div
                key={c.customerId}
                className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-xs"
              >
                <p className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate font-semibold text-neutral-100">
                    {c.customerName}
                  </span>
                  <span className="shrink-0 text-neutral-500">
                    {c.count} {t("chantier.invColCount")}
                  </span>
                </p>
                <p className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[11px]">
                  <span className="text-neutral-300">
                    {t("chantier.invColTotal")} {m(c.total)}
                  </span>
                  <span className="text-lime">
                    {t("chantier.invColReceived")} {m(c.received)}
                  </span>
                  <span className="text-amber-300">
                    {t("chantier.invColOpen")} {m(c.openTotal)}
                  </span>
                  {c.overdueCount > 0 && (
                    <span className="text-red-300">
                      ⚠ {t("chantier.invColOverdue")} {c.overdueCount} · {m(c.overdueTotal)}
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
          <div
            className={`rounded-lg border px-3 py-2 ${
              overdue.length > 0
                ? "border-red-500/50 bg-red-500/10"
                : "border-neutral-800 bg-neutral-900"
            }`}
            data-testid="chantier-overdue"
          >
            <p className="text-sm font-bold text-red-200">
              ⏰ {t("chantier.invOverdueTitle")} · {overdue.length}
            </p>
            {overdue.length === 0 ? (
              <p className="text-[11px] text-neutral-400">{t("chantier.invOverdueNone")}</p>
            ) : (
              <div className="mt-1 space-y-1">
                {overdue.map((f) => (
                  <p key={f.id} className="flex justify-between gap-2 text-xs text-red-100/90">
                    <span className="min-w-0 truncate">
                      <span className="font-mono">{f.invoiceNumber}</span>
                      <span className="text-red-200/60"> · {f.customerName}</span>
                      <span className="block text-[10px] text-red-200/60">
                        {t("chantier.invDue")} {f.dueAt} · {f.daysLate} {t("chantier.invDaysLate")}
                      </span>
                    </span>
                    <span className="shrink-0 font-mono">{m(f.amount)}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** Repasse consolidado do responsável: carros dsp do mês, particular × pro. */
function RepasseSection({ companyId }: { companyId: number }) {
  const t = useT();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const q = trpc.chantierPortal.myRepasse.useQuery({ companyId, month });
  const totals = q.data?.totals;
  const cars = q.data?.cars ?? [];
  // F-gerente-entrega — faturados sem entrega registrada (comissão parada).
  const semEntrega = q.data?.undelivered ?? [];
  if (!q.isLoading && cars.length === 0 && totals?.all.count === 0) {
    // Sem chantier configurado para este usuário OU mês vazio — mostra mesmo
    // assim quando há configuração (mês vazio também é informação).
  }
  return (
    <section className="pt-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-xs uppercase tracking-wider text-neutral-500">
          💶 {t("chantier.repasseTitle")}
        </h2>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs"
        />
      </div>
      <p className="mb-2 text-[11px] text-neutral-500">{t("chantier.repasseByDelivery")}</p>
      {semEntrega.length > 0 && (
        <div
          className="mb-2 space-y-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2"
          data-testid="repasse-undelivered"
        >
          <p className="text-sm font-semibold text-amber-200">
            ⚠ {semEntrega.length} {t("chantier.undeliveredPre")}{" "}
            {formatMoney(q.data?.undeliveredTotal ?? 0, { decimals: 2 })}{" "}
            {t("chantier.undeliveredSuf")}
          </p>
          {semEntrega.map((c) => (
            <p key={c.dossierId} className="flex justify-between gap-2 text-xs text-amber-100/90">
              <span className="min-w-0 truncate">
                <span className="font-mono">{c.plate ?? "—"}</span>
                <span className="text-amber-200/60">
                  {" "}
                  · {c.vehicle || "—"} · {c.customerName}
                </span>
              </span>
              <span className="shrink-0 font-mono">
                {formatMoney(c.commission, { decimals: 2 })}
              </span>
            </p>
          ))}
        </div>
      )}
      {q.isLoading ? (
        <p className="text-sm text-neutral-600">…</p>
      ) : cars.length === 0 ? (
        <p className="text-sm text-neutral-600">{t("chantier.repasseEmpty")}</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                [t("chantier.repassePro"), totals!.pro],
                [t("chantier.repasseParticular"), totals!.particular],
                [t("chantier.repasseTotal"), totals!.all],
              ] as const
            ).map(([label, tt]) => (
              <div
                key={label}
                className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2"
              >
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">
                  {label} · {tt.count}
                </p>
                <p className="font-mono text-sm font-bold text-lime">
                  {formatMoney(tt.commission, { decimals: 2 })}
                </p>
              </div>
            ))}
          </div>
          <div className="space-y-1">
            {cars.map((c) => (
              <div
                key={`${c.dossierId}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-sm"
              >
                <span className="min-w-0 truncate">
                  <span className="font-mono">{c.plate ?? "—"}</span>
                  <span className="text-neutral-500">
                    {" "}
                    · {c.vehicle || "—"} · {c.customerName}
                  </span>
                  <span
                    className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] ${
                      c.particular
                        ? "bg-blue-500/15 text-blue-300"
                        : "bg-neutral-800 text-neutral-400"
                    }`}
                  >
                    {c.particular ? t("chantier.repasseParticular") : t("chantier.repassePro")}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-mono text-xs text-neutral-500">
                    {formatMoney(c.baseHt, { decimals: 2 })} × {c.pct}%
                  </span>
                  <span className="block font-mono font-semibold text-lime">
                    {formatMoney(c.commission, { decimals: 2 })}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function CheckForm({
  kind,
  companyId,
  dossierId,
  incomplete = false,
  franchiseRequired,
  customerName = "",
  classificarCliente = false,
  clienteRemiseAtual = null,
  customerWithoutChantier = false,
  chantierId = null,
  chantierName = null,
  existing,
  savedAt,
  saveError,
  saving,
  onSave,
}: {
  kind: "reception" | "delivery";
  companyId: number;
  dossierId: number;
  /**
   * F-recepcao-status — gravado, porém ainda sem tudo o que a trava do reparo
   * exige. O selo verde aqui era o que fazia o gerente achar que o carro
   * estava liberado enquanto o técnico não conseguia assumir.
   */
  incomplete?: boolean;
  /** F1 franquia — o campo só aparece quando exigida (particular sempre). */
  franchiseRequired: boolean;
  /** F-cliente-sem-chantier — pergunta "ligar o cliente a este chantier?". */
  customerName?: string;
  /**
   * CEO 2026-11-04 ("Xavier define o tipo, a porcentagem e insere no
   * chantier") — cliente "a revisar" ou Pro sem remise: passo obrigatório.
   */
  classificarCliente?: boolean;
  clienteRemiseAtual?: string | null;
  customerWithoutChantier?: boolean;
  chantierId?: number | null;
  chantierName?: string | null;
  existing: FoundDossier["checks"][number] | null;
  /** Momento em que ESTE check foi gravado (null = não foi agora). */
  savedAt: number | null;
  /** Erro do último save DESTE check — mostrado ao lado do botão. */
  saveError: string | null;
  saving: boolean;
  onSave: (p: SavePayload) => void;
}) {
  const t = useT();
  // F-cliente-sem-chantier — CEO 2026-10-11: sem pergunta; a recepção liga o
  // cliente ao chantier do gerente. Aqui só se avisa.
  const perguntaChantier = kind === "reception" && customerWithoutChantier && chantierId != null;
  const [open, setOpen] = useState(false);
  // Gravou → recolhe e mostra o resumo com o ✓, em vez de deixar o
  // formulário aberto com o botão ativo como se nada tivesse acontecido.
  useEffect(() => {
    if (savedAt != null) setOpen(false);
  }, [savedAt]);
  const [damages, setDamages] = useState<string[]>(existing?.damages ?? []);
  const [damageInput, setDamageInput] = useState("");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  // F-franquia-primeiro (CEO 2026-09-29) — resposta EXPLÍCITA (sim/não), não
  // um checkbox que ninguém nota: vazio = ainda não respondeu.
  const [franchiseAns, setFranchiseAns] = useState<"" | "yes" | "no">(
    existing == null ? "" : existing.franchise ? "yes" : "no",
  );
  const franchise = franchiseAns === "yes";
  // CEO 2026-11-04 — resposta EXPLÍCITA do gerente (vazio = não respondeu).
  const [cliTipo, setCliTipo] = useState<"" | "individual" | "business">("");
  const [cliRemise, setCliRemise] = useState(
    clienteRemiseAtual != null ? String(Number(clienteRemiseAtual)) : "",
  );
  const pedeCliente = kind === "reception" && classificarCliente;
  const remiseNum = Number(cliRemise.replace(",", "."));
  const clienteRespondido =
    !pedeCliente ||
    cliTipo === "individual" ||
    (cliTipo === "business" &&
      cliRemise.trim() !== "" &&
      Number.isFinite(remiseNum) &&
      remiseNum >= 0 &&
      remiseNum <= 100);
  const [franchiseAmount, setFranchiseAmount] = useState(
    existing?.franchiseAmount != null ? String(Number(existing.franchiseAmount)) : "",
  );
  const [mileageKm, setMileageKm] = useState(
    existing?.mileageKm != null ? String(existing.mileageKm) : "",
  );
  const [loaner, setLoaner] = useState(existing?.loanerVehicle ?? false);
  const [loanerInfo, setLoanerInfo] = useState(existing?.loanerVehicleInfo ?? "");
  const [workOk, setWorkOk] = useState<boolean>(existing?.workOk ?? false);
  // F2 checklist — pára-brisa/ciel + assinatura (fotos sobem na hora).
  const [chk, setChk] = useState<ChecklistDraft>({
    mileage: "",
    pareBrise: existing?.pareBriseOk == null ? "" : existing.pareBriseOk ? "ok" : "nok",
    ciel: existing?.cielOk == null ? "" : existing.cielOk ? "ok" : "nok",
    signature: null,
    signedName: "",
  });
  const isReception = kind === "reception";
  const done = existing != null;
  // F-fotos-entrega (CEO 2026-09-29) — as 5 poses da recepção, na entrega.
  // Só cobra na entrega NOVA (o servidor faz a mesma conta).
  const deliveryPhotosQ = trpc.chantierPortal.listChecklistPhotos.useQuery(
    { companyId, dossierId, checkKind: "delivery" },
    { enabled: !isReception },
  );
  const deliveryPhotosMissing = isReception
    ? 0
    : MONTAGE_PHOTO_SLOTS.filter(
        (k) => !(deliveryPhotosQ.data?.rows ?? []).some((p) => p.kind === k),
      ).length;
  const deliveryBlocked = !isReception && !done && deliveryPhotosMissing > 0;

  function addDamage() {
    const v = damageInput.trim();
    if (!v) return;
    setDamages((prev) => [...prev, v]);
    setDamageInput("");
  }

  return (
    <div
      className={`rounded-lg border p-3 space-y-2 ${
        done && incomplete
          ? "border-amber-500/40 bg-amber-500/5"
          : done
            ? "border-lime/30 bg-lime/5"
            : "border-neutral-800 bg-neutral-950/60"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-semibold">
          {isReception ? "📥 " : "📤 "}
          {isReception ? t("chantier.receptionTitle") : t("chantier.deliveryTitle")}
          {done &&
            (incomplete ? (
              <span className="ml-2 text-xs text-amber-300">⚠ {t("chantier.incompleteBadge")}</span>
            ) : (
              <span className="ml-2 text-xs text-lime">✓ {t("chantier.doneBadge")}</span>
            ))}
        </span>
        <span className="text-neutral-500">{open ? "▲" : "▼"}</span>
      </button>
      {done && !open && pedeCliente && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full rounded-md border-2 border-sky-500 bg-sky-500/10 px-2.5 py-2 text-left text-xs font-semibold text-sky-100"
          data-testid="chantier-customer-pending"
        >
          ⚠ {t("chantier.customerPendingPre")} <strong>{customerName}</strong>{" "}
          {t("chantier.customerPendingSuf")}
        </button>
      )}
      {done && !open && (
        <>
          <p className="text-xs text-neutral-400">
            {existing.damages.length > 0 &&
              `${t("chantier.damagesLabel")}: ${existing.damages.join("; ")} · `}
            {!isReception && existing.workOk != null && (
              <span className={existing.workOk ? "text-lime" : "text-amber-300"}>
                {existing.workOk ? t("chantier.workOkYes") : t("chantier.workOkNo")}
              </span>
            )}
          </p>
          {/* F-franquia-visivel (CEO 2026-09-21: "continuo não vendo a opção
              para modificar franquia") — a franquia morava DENTRO do cartão
              recolhido: quem não soubesse tocar no título para expandir não
              via nem o valor nem como mudá-lo. Agora ela aparece no resumo,
              com o botão que abre o formulário. */}
          {isReception && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex w-full items-center justify-between gap-2 rounded-md border border-neutral-800 bg-neutral-950/60 px-2.5 py-1.5 text-xs"
            >
              <span>
                <span className="text-neutral-500">{t("chantier.franchiseLabel")}: </span>
                {existing.franchise ? (
                  <span className="font-mono text-amber-200">
                    {existing.franchiseAmount != null
                      ? formatMoney(Number(existing.franchiseAmount), { decimals: 2 })
                      : "—"}
                  </span>
                ) : (
                  <span className="text-neutral-300">{t("chantier.franchiseNone")}</span>
                )}
              </span>
              <span className="shrink-0 text-lime">{t("chantier.franchiseChangeBtn")}</span>
            </button>
          )}
        </>
      )}
      {open && (
        <div className="space-y-2">
          {/* CEO 2026-11-04 — o cliente veio do eDSP sem tipo/remise conferidos:
              sem isso a comissão do técnico fica "à calculer" (GD-245-KQ). */}
          {pedeCliente && (
            <div
              className="space-y-2 rounded-lg border-2 border-sky-500 bg-sky-500/10 p-3"
              data-testid="chantier-customer-step"
            >
              <p className="text-[11px] font-bold uppercase tracking-wider text-sky-300">
                {t("chantier.customerStep")}
              </p>
              <p className="text-sm font-semibold text-sky-100">
                {t("chantier.customerQPre")} <strong>{customerName}</strong>{" "}
                {t("chantier.customerQSuf")}
              </p>
              <div className="flex flex-wrap gap-2">
                {(["individual", "business"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setCliTipo(v)}
                    className={`rounded-md border px-4 py-2 text-sm font-bold ${
                      cliTipo === v
                        ? "border-sky-400 bg-sky-500 text-neutral-950"
                        : "border-sky-500/60 bg-neutral-950 text-sky-200"
                    }`}
                    data-testid={`chantier-customer-${v}`}
                  >
                    {v === "individual"
                      ? t("chantier.customerIndividual")
                      : t("chantier.customerBusiness")}
                  </button>
                ))}
              </div>
              {cliTipo === "business" && (
                <label className="flex items-center gap-2 text-sm text-sky-100">
                  {t("chantier.customerRemiseLabel")}
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={cliRemise}
                    onChange={(e) => setCliRemise(e.target.value)}
                    className="w-24 rounded-md border border-sky-500/60 bg-neutral-950 px-3 py-2 font-mono text-sm outline-none focus:border-lime"
                    data-testid="chantier-customer-remise"
                  />
                  %
                </label>
              )}
              <p className="text-[11px] text-sky-200/80">{t("chantier.customerStepHint")}</p>
            </div>
          )}
          {/* F-franquia-primeiro (CEO 2026-09-29: "a validação da franquia deve
              ser evidente e obrigatória antes das fotos") — passo 1 da
              recepção, para TODO cliente; as fotos só abrem depois. */}
          {isReception && (
            <div
              className="space-y-2 rounded-lg border-2 border-amber-500 bg-amber-500/10 p-3"
              data-testid="chantier-franchise-step"
            >
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-300">
                {t("chantier.franchiseStep")}
                {franchiseRequired && (
                  <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-amber-200">
                    {t("chantier.franchiseRequiredBadge")}
                  </span>
                )}
              </p>
              <p className="text-sm font-semibold text-amber-100">{t("chantier.franchiseQ")}</p>
              <div className="flex gap-2">
                {(["yes", "no"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFranchiseAns(v)}
                    className={`rounded-md border px-4 py-2 text-sm font-bold ${
                      franchiseAns === v
                        ? "border-amber-400 bg-amber-500 text-neutral-950"
                        : "border-amber-500/60 bg-neutral-950 text-amber-200"
                    }`}
                  >
                    {v === "yes" ? t("chantier.franchiseYes") : t("chantier.franchiseNo")}
                  </button>
                ))}
              </div>
              {franchise && (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={franchiseAmount}
                  onChange={(e) => setFranchiseAmount(e.target.value)}
                  placeholder={t("chantier.franchiseAmountPh")}
                  className="w-full rounded-md border border-amber-500/60 bg-neutral-950 px-3 py-2 font-mono text-sm outline-none focus:border-lime"
                />
              )}
            </div>
          )}
          <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
            {isReception ? t("chantier.damagesLabel") : t("chantier.reservesLabel")}
          </label>
          <div className="flex gap-1.5">
            <input
              value={damageInput}
              onChange={(e) => setDamageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDamage();
                }
              }}
              placeholder={t("chantier.damagePlaceholder")}
              className="min-w-0 flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
            />
            <button
              type="button"
              onClick={addDamage}
              className="rounded-md border border-neutral-700 px-3 text-sm hover:border-lime"
            >
              +
            </button>
          </div>
          {damages.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {damages.map((dm, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 text-xs text-amber-200"
                >
                  {dm}
                  <button
                    type="button"
                    onClick={() => setDamages((prev) => prev.filter((_, j) => j !== i))}
                    className="text-amber-400"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* F-cliente-sem-chantier — a franquia subiu para o passo 1 (acima). */}
          {isReception && perguntaChantier && (
            <p
              className="rounded-md border border-sky-700/50 bg-sky-500/10 px-3 py-2 text-xs text-sky-100"
              data-testid="attach-chantier-auto"
            >
              {t("chantier.attachChantierAutoPre")} <strong>{customerName}</strong>{" "}
              {t("chantier.attachChantierAutoSuf")} <strong>{chantierName ?? ""}</strong>.
            </p>
          )}
          {isReception && (
            <div className="space-y-1.5 rounded-md border border-neutral-800 p-2">
              <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
                {t("chantier.mileageLabel")}
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={mileageKm}
                onChange={(e) => setMileageKm(e.target.value)}
                placeholder={t("chantier.mileagePh")}
                className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 font-mono text-sm outline-none focus:border-lime"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={loaner}
                  onChange={(e) => setLoaner(e.target.checked)}
                />
                {t("chantier.loanerToggle")}
              </label>
              {loaner && (
                <input
                  value={loanerInfo}
                  onChange={(e) => setLoanerInfo(e.target.value)}
                  placeholder={t("chantier.loanerPh")}
                  className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                />
              )}
            </div>
          )}
          {isReception && franchiseAns === "" && (
            <p className="rounded-md border border-amber-500/40 px-3 py-2 text-xs font-semibold text-amber-300">
              🔒 {t("chantier.franchiseFirstHint")}
            </p>
          )}
          {!clienteRespondido && (
            <p className="rounded-md border border-sky-500/40 px-3 py-2 text-xs font-semibold text-sky-300">
              🔒 {t("chantier.customerFirstHint")}
            </p>
          )}
          {isReception && franchiseAns !== "" && (
            <div className="space-y-1.5 rounded-md border border-neutral-800 p-2">
              <ChantierChecklistExtras
                companyId={companyId}
                dossierId={dossierId}
                value={chk}
                onChange={setChk}
                alreadySigned={existing?.signed ?? false}
              />
            </div>
          )}
          {!isReception && <ChantierDeliveryPhotos companyId={companyId} dossierId={dossierId} />}
          {!isReception && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={workOk}
                onChange={(e) => setWorkOk(e.target.checked)}
              />
              {t("chantier.workOkToggle")}
            </label>
          )}
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("chantier.notesPh")}
            rows={2}
            className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
          />
          <button
            type="button"
            disabled={
              saving ||
              deliveryBlocked ||
              !clienteRespondido ||
              (isReception && franchiseAns === "") ||
              (isReception &&
                franchise &&
                (franchiseAmount.trim() === "" || Number(franchiseAmount) <= 0))
            }
            onClick={() =>
              onSave({
                kind,
                damages,
                notes: notes.trim() || undefined,
                franchise: isReception ? franchise : false,
                franchiseAmount:
                  isReception && franchise && franchiseAmount.trim() !== ""
                    ? Number(franchiseAmount)
                    : null,
                mileageKm:
                  isReception && mileageKm.trim() !== "" ? Math.round(Number(mileageKm)) : null,
                loanerVehicle: isReception ? loaner : null,
                loanerVehicleInfo: isReception && loaner ? loanerInfo.trim() || null : null,
                pareBriseOk: isReception && chk.pareBrise !== "" ? chk.pareBrise === "ok" : null,
                cielOk: isReception && chk.ciel !== "" ? chk.ciel === "ok" : null,
                signatureBase64: isReception ? chk.signature : null,
                signedByName: isReception ? chk.signedName.trim() || null : null,
                chantierId: isReception && chantierId != null ? chantierId : undefined,
                workOk: isReception ? null : workOk,
                ...(pedeCliente && cliTipo !== ""
                  ? {
                      customerKind: cliTipo,
                      customerRemisePct: cliTipo === "business" ? remiseNum : null,
                    }
                  : {}),
              })
            }
            className="w-full rounded-lg bg-lime px-4 py-2.5 text-sm font-semibold text-on-lime disabled:opacity-40"
          >
            {saving ? t("chantier.savingBtn") : t("chantier.saveBtn")}
          </button>
          {deliveryBlocked && (
            <p className="text-[11px] text-amber-300">
              📷 {t("chantier.deliveryPhotosMissingPre")} {deliveryPhotosMissing}{" "}
              {t("chantier.deliveryPhotosMissingSuf")}
            </p>
          )}
          {saveError && (
            <p className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
              ✕ {saveError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * F-fotos-entrega (CEO 2026-09-29) — "o gerente de chantier deve fazer [fotos]
 * no início, na recepção, e na entrega para controlar o trabalho". Mesmas 5
 * poses e mesma ordem da recepção: antes × depois lado a lado.
 */
function ChantierDeliveryPhotos({
  companyId,
  dossierId,
}: {
  companyId: number;
  dossierId: number;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const photosQuery = trpc.chantierPortal.listChecklistPhotos.useQuery({
    companyId,
    dossierId,
    checkKind: "delivery",
  });
  const uploadMut = trpc.chantierPortal.uploadChecklistPhoto.useMutation({
    onSuccess: () => utils.chantierPortal.listChecklistPhotos.invalidate(),
  });
  const [uploadingKind, setUploadingKind] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  async function handleUpload(kind: PhotoSlotKind, file: File) {
    setUploadingKind(kind);
    setUploadErr(null);
    try {
      const b64 = await compressImageToBase64(file);
      await uploadMut.mutateAsync({
        companyId,
        dossierId,
        checkKind: "delivery",
        kind,
        contentBase64: b64,
      });
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploadingKind(null);
    }
  }
  return (
    <div className="space-y-1.5">
      <GuidedPhotoLine
        title={t("chantier.deliveryPhotosTitle")}
        sequence={MONTAGE_PHOTO_SLOTS}
        photos={photosQuery.data?.rows ?? []}
        onUploadPhoto={handleUpload}
        uploadingKind={uploadingKind}
        tone="dark"
      />
      {uploadErr && (
        <p className="rounded-md bg-red-500/15 px-3 py-2 text-xs font-semibold text-red-300">
          ✕ {uploadErr}
        </p>
      )}
    </div>
  );
}

/**
 * F2 checklist — bloco compartilhado (fotos guiadas + toggles + assinatura)
 * com upload via tRPC do PORTAL DO CHANTIER (compressão no aparelho).
 */
function ChantierChecklistExtras({
  companyId,
  dossierId,
  value,
  onChange,
  alreadySigned,
}: {
  companyId: number;
  dossierId: number;
  value: ChecklistDraft;
  onChange: (v: ChecklistDraft) => void;
  alreadySigned: boolean;
}) {
  const utils = trpc.useUtils();
  const photosQuery = trpc.chantierPortal.listChecklistPhotos.useQuery({
    companyId,
    dossierId,
    checkKind: "reception",
  });
  const uploadMut = trpc.chantierPortal.uploadChecklistPhoto.useMutation({
    onSuccess: () => utils.chantierPortal.listChecklistPhotos.invalidate(),
  });
  const [uploadingKind, setUploadingKind] = useState<string | null>(null);
  async function handleUpload(kind: PhotoSlotKind, file: File) {
    setUploadingKind(kind);
    try {
      const b64 = await compressImageToBase64(file);
      await uploadMut.mutateAsync({
        companyId,
        dossierId,
        checkKind: "reception",
        kind,
        contentBase64: b64,
      });
    } catch {
      // erro de rede/tamanho: o slot volta vazio e o responsável tenta de novo
    } finally {
      setUploadingKind(null);
    }
  }
  return (
    <ChecklistExtras
      value={value}
      onChange={onChange}
      photos={photosQuery.data?.rows ?? []}
      onUploadPhoto={handleUpload}
      uploadingKind={uploadingKind}
      alreadySigned={alreadySigned}
      showMileage={false}
      tone="dark"
    />
  );
}
